import _ from 'lodash';
import { CREATE_USER_FEATURE_FROM_QUESTION } from '../../mutations/UserFeature';
import { CountryKey } from '../../../utils/wpo/codes';
import { FetchingState } from '../Fetching/FetchingState';
import { GET_DYNAMIC_NEXT_QUESTION, GET_QUESTION_BY_ID } from '../../queries/Survey';
import { IQuestionModel, QuestionModel } from './QuestionModel';
import { IResponse } from '../Response/Response';
import { IStaticFrameModel } from './StaticFrameModel';
import { ITreeNav } from '../TreeNav/TreeNav';
import { Instance, SnapshotOut, cast, flow, getParent, getRoot, types } from 'mobx-state-tree';
import { RootStoreModel } from '../Root/RootStore';
import { captureException } from '../../../utils/sentry/sentry';
import { clientFunction } from '../../../config/apollo/client';
import { flagAndName } from '../../../components/Answer/AnswerSelect/Container/ContainRegionInstantHelp';
import { getMax } from '../../../components/QuestionSessions/QuestionSession/utils';
import { hasError, isReady } from '../Fetching/updateStateHelper';
import { makeRequestId } from '../../../utils/others/makeRequestId';

export interface FetchQuestionOptions {
  id: string;
  withDynamicAnswers?: boolean;
  surveyResponses?: SnapshotOut<IResponse>[];
  mode?: 'overwrite' | 'appendAnswers';
}
const disableStepBackFor = ['appointmentCreated', 'appointmentError'];

const formatAnswers  = (question: any) => {
  let answers = null;
  if (question && question.answers && question.answers.length > 0) {
    answers = question.answers.map(a => ({
      ...a,
      featureChoice: a?.featureChoice?._id,
      nextKey: a.nextQuestionKey
    }));
  }
  return answers;
};
const formatStaticFrame  = (question: any) : IStaticFrameModel | undefined => {
  if(question?.type === 'staticFrame') {
    const staticFrameData= question?.staticFrame;
    return {
      type : staticFrameData?.type,
      // MST's `types.maybe(string)` accepts undefined, not null — coerce nulls
      // coming back from GraphQL so questions without an image don't blow up.
      image: staticFrameData?.image ?? undefined,
      emoji: staticFrameData?.emoji ?? undefined,
      mainTitle: staticFrameData?.title?.value,
      secondaryTitle: staticFrameData?.title?.secondaryValue,
      thirdTitle: staticFrameData?.title?.thirdValue,
      text: staticFrameData?.text?.map((t: any) => ({ value: t.value, secondaryValue: t.secondaryValue, thirdValue: t.thirdValue})) || []
    };
  }
};
const extractResponses = (responses: IResponse[] | null, choices?: IResponse): SnapshotOut<IResponse>[] => {
  responses = responses ? responses : [];
  if (choices) {
    const index = responses.findIndex(r => r.questionId === choices.questionId);
    if (index === -1) {
      responses.push(choices);
    } else {
      responses[index] = choices;
    }
  }
  return responses;
};

export const QuestionStore = types
  .model('QuestionStore', {
    question: types.array(QuestionModel),
    states: types.optional(types.array(FetchingState),[]),
  })
  .actions(self => {
    const loadQuestion = (question: any) => {
      const rootStore = getParent<any>(self);
      const defaultNextKey = question.defaultNextQuestionKey;

      const answers= formatAnswers(question);
      const staticFrame = formatStaticFrame(question);

      const questionData = {
        ...question,
        answers,
        staticFrame,
        language: rootStore.language.name.key,
        feature: question.feature?._id,
        defaultNextKey: defaultNextKey,
        survey: question.survey?._id,
        disableStepBack: disableStepBackFor.includes(question.key),
      };

      const index = self.question?.findIndex(c => c._id === question._id);
      try {
        if (index === -1) {
          self.question?.push(questionData);
        } else {
          self.question[index] = questionData;
        }
      } catch (err) {
        console.error('Could not load question' + question._id);
        console.error(JSON.stringify(questionData, undefined, 2));
        throw err;
      }
    };
    const appendAnswers = (question: any) => {
      const index = self.question?.findIndex(q => q._id === question._id);
      if (index === -1) {
        console.error(`Could not append answers. Question id not found ${question._id}`);
        return;
      }
      const answers = formatAnswers(question);
      // Dedup by _id. This used to blindly concatenate, so fetching the same
      // question twice (e.g. re-entering the assistance wizard) doubled every
      // option - which is why "Mode of interaction?" showed
      // "By phone / By videocall / By phone / By videocall". The seed data is
      // correct: each of those questions has exactly 2 answers.
      const existing = self.question[index].answers || [];
      const seen = new Set(existing.map(a => a?._id));
      const merged = [...existing, ...(answers || []).filter((a: any) => a && !seen.has(a._id))];
      self.question[index].answers = cast(merged);
    };
    const fetchQuestion = flow(function* (opts: string | FetchQuestionOptions) {
      if (typeof opts === 'string') {
        opts = {id: opts};
      }
      const {id, withDynamicAnswers, surveyResponses, mode = 'overwrite'} = opts;

      // Clear stale answers when re-fetching with dynamic answers so the UI
      // shows a loading spinner instead of previous (wrong) answers.
      if (withDynamicAnswers && mode === 'overwrite') {
        const existingIndex = self.question?.findIndex(q => q._id === id);
        if (existingIndex !== undefined && existingIndex !== -1) {
          self.question[existingIndex].answers = cast(null);
        }
      }

      updateState(id, 'pending');
      try {
        const { data }: any = yield clientFunction((getParent(self) as any).language.name.key)
          .query({
            query: GET_QUESTION_BY_ID,
            variables: {
              id,
              withDynamicAnswers,
              // workaround for strict gql input type (no extra fields allowed)
              surveyResponses: surveyResponses?.map(r => ({...r, surveyId: undefined, valueSonar: undefined, sessionId: undefined, pathId: undefined})),
            }
          });
        if (data) {
          if (mode === 'overwrite') {
            loadQuestion(data.question);
          } else if (mode === 'appendAnswers') {
            appendAnswers(data.question);
          }
        }
        updateState(id, 'done');
      } catch (error) {
        updateState(id, 'error');
        captureException(error);
      }
    });
    const fetchQuestionIfNotExist = flow(function* (id: string) {
      if (self.question) {
        const index = self.question.findIndex(p => p._id === id);
        if (index === -1 || self.question[index].language !== (getParent(self) as any).language.name.key) {
          yield fetchQuestion(id);
        }
      } else {
        yield fetchQuestion(id);
      }
    });
    const fetchQuestionWithCurrentSurveyResponses : any = flow(function*(id: string, mode: 'overwrite' | 'appendAnswers' = 'overwrite') {
      const treeNav = getRoot<Instance<typeof RootStoreModel>>(self).treeNav as ITreeNav;
      yield fetchQuestion({
        id,
        withDynamicAnswers: true,
        surveyResponses: extractResponses(treeNav.choices.finalised || [], treeNav.choices.pending),
        mode,
      });
    });
    const fetchNextQuestion: any = flow(function*() {
      const treeNav = getRoot<Instance<typeof RootStoreModel>>(self).treeNav as ITreeNav;
      if (!treeNav.content.nextQuestion) {
        return;
      }
      yield fetchQuestionWithCurrentSurveyResponses(treeNav.content.nextQuestion._id);
    });
    const fetchDynamicNextQuestion: (questionId: string) => Promise<IQuestionModel | undefined> = flow(function*(questionId: string) {
      const userId = getRoot<Instance<typeof RootStoreModel>>(self).user.user?._id;
      const treeNav = getRoot<Instance<typeof RootStoreModel>>(self).treeNav as ITreeNav;
      // no guard on content.nextQuestion: the steps a dynamic question leads to
      // are often FRAME steps with no question behind them (confirmSession →
      // appointmentCreated / appointmentError), and skipping the call there
      // meant the WPO appointment was never created

      const surveyResponses = extractResponses(treeNav.choices.finalised || [], treeNav.choices.pending);

      updateState(questionId, 'pending');
      try {
        const { data }: any = yield clientFunction((getParent(self) as any).language.name.key)
          .query({
            query: GET_DYNAMIC_NEXT_QUESTION,
            variables: {
              input: {
                questionId,
                requestId: makeRequestId({fn: 'getDynamicNextQuestion', userId})
              },
              withDynamicAnswers: true,
              // workaround for strict gql input type (no extra fields allowed)
              surveyResponses: surveyResponses?.map(r => ({...r, surveyId: undefined, valueSonar: undefined, sessionId: undefined, pathId: undefined})),
            }
          });
        if (data) {
          // the next step may be a FRAME backed by a legacy staticFrame question
          // that no longer fits QuestionModel (appointmentCreated/Error): FRAME
          // steps never read it from the store, and failing to store it must
          // not lose its key, or the default edge (appointmentCreated) wins
          if (data.question?.type !== 'staticFrame') {
            try {
              loadQuestion(data.question);
            } catch (error) {
              captureException(error);
            }
          }
          updateState(questionId, 'done');
          return data.question;
        }
        updateState(questionId, 'done');
      } catch (error) {
        updateState(questionId, 'error');
        captureException(error);
      }
    });
    // TODO remove me it's replaced by treeNav.choices.updateFeature()
    const updateFeature: any = (questionId: string, val: any, reckoningId: null) => {
      if (questionId && val) {
        const question = self.question.find(q => q._id === questionId);
        if (question && question.feature) {
          if(question.type === 'singleSelectWithRadio') {
            getRoot<Instance<typeof RootStoreModel>>(self).feature.updateWithSonar(question.feature, val?.value, val?.importance);
          } else if (question?.type === 'range') {
            getRoot<Instance<typeof RootStoreModel>>(self).feature.updateWithVal(question.feature, val);
          } else if (question?.type === 'freeText') {
            getRoot<Instance<typeof RootStoreModel>>(self).feature.updateWithFreeText(question.feature, val);
          } else {
            if (val.length > 0) {
              const answers = val.map(v => question.answers?.find(a => a._id === v)?.featureChoice);
              getRoot<Instance<typeof RootStoreModel>>(self).feature.updateWithAnswers(question.feature, answers);
            }

          }
        }
      }
    };

    const updateFeatureServerAndRefetchSegments: any = flow(function* (questionId: string, val: any, sessionId: string, reckoningId= null, refetchAssistanceItems = false) {
      yield updateFeatureServer(questionId, val, reckoningId);
      yield getRoot<Instance<typeof RootStoreModel>>(self)?.session.refetchUserSegments(sessionId);
      if(refetchAssistanceItems) {
        yield getRoot<Instance<typeof RootStoreModel>>(self)?.user.fetchOwnAssistanceItems( getParent(self as any)?.language?.language);
      }
    });


    // TODO remove me it's replaced by treeNav.choices.updateFeatureServer()
    const updateFeatureServer = flow(function* (questionId: string, val: any, reckoningId= null) {
      if(!questionId || !val) return;
      const question = self.question.find(q => q._id === questionId);
      if(!question || !question.feature) return;
      let value = null;
      let answers = null;
      if (question?.type === 'singleSelect' || question?.type === 'multiSelect') {
        if(val.length === 0) return;
        answers = val.map(v => question.answers?.find(a => a._id === v)?.featureChoice);
      }
      switch (question?.type) {
      case 'range': value = { value: val }; break;
      case 'singleSelect':
      case 'singleSelectWithAnswer':
        value = { valueSingleSelect: answers[0] }; break;
      case 'singleSelectWithRadio':
        value = { value:val.valueSonar , importance: val.importance }; break;
      case 'multiSelect': value = { valueMultiSelect: answers }; break;
      case 'freeText': value = { valueFreeText: val }; break;
      }
      yield clientFunction((getParent(self) as any).language.name.key).mutate({
        mutation: CREATE_USER_FEATURE_FROM_QUESTION,
        fetchPolicy:'no-cache',
        variables: {
          feature: question.feature,
          question: questionId,
          reckoning: reckoningId,
          ...value
        }
      });
    });

    const replaceNextKeys = (surveyId: string, mapping: [key: string, replacement: string][]) => {
      const questions = self.question.filter(q => q.survey === surveyId);
      for (const [key, replacement] of mapping) {
        for (const question of questions) {
          if (question.defaultNextKey === key) {
            console.log(`Replacing defaultNextKey ${key} with ${replacement} in question ${question.key}`);
            question.defaultNextKey = replacement;
          }
          for (const answer of (question.answers || [])) {
            if (answer.nextKey === key) {
              console.log(`Replacing nextKey ${key} with ${replacement} in answer ${answer.key}`);
              answer.nextKey = replacement;
            }
          }
        }
      }
    };

    const updateState = (id: string, newState: string)=> {
      const index = self.states.findIndex(s => s.id === id);
      if (index === -1) {
        self.states.push({ id, state: newState });
      } else {
        self.states[index].state = newState;
      }
    };

    const  setErrors = (id: [string]) => {
      id.forEach(i => setError(i));
    };

    const  setError = (id: string) => {
      if(self.states && Array.isArray(self.states)) {
        const index = self.states.findIndex(s => s.id === id);
        if (index !== -1 && self.states[index].state === 'pending') {
          self.states[index].state = 'error';
        }
      }
    };

    const overrideStaticFrame = (id: string, staticFrame: IStaticFrameModel) => {
      const question = self.question.find(q => q._id === id);
      if (!question) {
        throw new Error(`Could not find question with id (${id})`);
      }
      question.staticFrame = staticFrame;
      question.type == 'staticFrame';
    };




    const clear = () => {
      self.question = cast([]);
      self.states = cast([]);
    };
    return {
      loadQuestion,
      fetchQuestion,
      fetchQuestionIfNotExist,
      fetchQuestionWithCurrentSurveyResponses,
      fetchNextQuestion,
      fetchDynamicNextQuestion,
      updateFeature,
      updateFeatureServer,
      updateFeatureServerAndRefetchSegments,
      replaceNextKeys,
      overrideStaticFrame,
      setError,
      setErrors,
      clear
    };
  })
  .views(self => ({
    /**
     * Substitutes the {{region}} placeholder in a question's text.
     *
     * Lives on its own because question text reaches the UI by two routes: through
     * getQuestion() for session steps, and straight off `currentDataRef.question`
     * in TreeNavContent for everything else -- including the instant-session
     * wizard, which is why users saw a literal "Are you currently located in
     * {{region}}?". Both routes call this now.
     *
     * Takes an already-cloned question and mutates it, so callers keep ownership of
     * the copy and the store's own snapshot is never touched.
     */
    withRegion<T extends {question?: string | null} | null | undefined>(question: T): T {
      if(question && question.question) {
        const region = (getParent<Instance<typeof RootStoreModel>>(self)).user.user?.region;
        const labels = (getParent<Instance<typeof RootStoreModel>>(self)).language.translate.MOBILE?.ASSISTANCE?.INSTANT_HELP;
        // Non-mutating: returning a copy means a future caller passing a LIVE MST
        // node gets correct behaviour instead of an "outside of actions" throw.
        return {
          ...question,
          question: question.question.replace('{{region}}',flagAndName(region as CountryKey, labels?.REGIONS)),
        };
      }
      return question;
    },
    getQuestion(id?: string) {
      const question = this.withRegion(_.cloneDeep(self.question?.find(q => q._id === id)));
      return question ? question : null;
    },
    getQuestionKey(id: string) {
      const question = this.getQuestion(id);
      return question ? question.key : null;
    },
    getAnswer(qId: string, aId: string) {
      const question = this.getQuestion(qId);
      const answer = question?.answers?.find(a => a._id === aId);
      return answer ? answer : null;
    },
    getAnswerKey(qId: string, aId: string) {
      const answer = this.getAnswer(qId, aId);
      return answer ? answer.key : null;
    },
    getQuestionAnswerKey(qId: string, aId: string) {
      const questionKey = this.getQuestionKey(qId);
      const answer = this.getAnswerKey(qId, aId);
      const answerKey = answer?answer: aId;
      return { questionKey, answerKey };
    },
    getQuestionByKey(key: string, surveyId: string) {
      return this.getQuestionsOfSurvey(surveyId).find(q => q.key === key);
    },
    getLabel(id: string, value: number): string {
      const question = this.getQuestion(id);
      if (question && question.range && question.range.intervals && question.range.intervals.length > 0) {
        const maxValue = getMax(question.range.intervals);
        const interval = question.range.intervals.find(interval => {
          return (
            (interval.minValue <= value && interval.maxValue > value) ||
            (interval.maxValue === maxValue && maxValue === value)
          );
        });
        if (interval) {
          return interval.label.name;
        } else {
          return '';
        }
      } else {
        return '';
      }
    },
    getQuestionsOfSurvey(surveyId: string): IQuestionModel[] {
      return self.question.filter(q => q.survey === surveyId).sort((a: any, b: any) => a.order > b.order ? 1 : -1);
    },
    hasError(id: string) {
      return hasError(self, id);
    },
    hasErrorPlural(id: string[]) {
      return id.every(i => hasError(self, i));
    },
    isReady(id: string) {
      return isReady(self, id);
    },
    isReadyPlural(id: string[]) {
      return id.every(i => isReady(self, i));
    },
    isQuestionUsingNewDesign(id: string) : boolean {
      const question = this.getQuestion(id);
      return !!question && getRoot<Instance<typeof RootStoreModel>>(self).survey.isSurveyUsingNewDesign(question.survey);
    }
  }));

export interface IQuestionStore extends Instance<typeof QuestionStore> {}
